import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import type { ClientSurveyAnswer, SurveyQuestion, SurveyTemplateDetail } from '../../types/survey';
import {
  filterAnswersForBatchSave,
  normalizeNumericAnswersForSave,
  getNumericCaptureInputProps,
  getNumericRangeHelperText,
  getOrderedQuestions,
  isAnswerableQuestion,
  isInstructionQuestion,
  isLikelySpinnerStepChange,
  isValidNumericAnswer,
  resolveNumericAnswerFromInput
} from '../../utils/surveyQuestions';

interface SurveyModalProps {
  open: boolean;
  template: SurveyTemplateDetail | null;
  initialAnswers: Record<number, ClientSurveyAnswer>;
  clientDisplayName: string;
  submitted: boolean;
  saving: boolean;
  error?: string;
  onClose: () => void;
  onSave: (answers: ClientSurveyAnswer[], submit: boolean) => void;
}

const SurveyModal: React.FC<SurveyModalProps> = ({
  open,
  template,
  initialAnswers,
  clientDisplayName,
  submitted,
  saving,
  error,
  onClose,
  onSave
}) => {
  const [answersMap, setAnswersMap] = useState<Record<number, ClientSurveyAnswer>>(initialAnswers);
  const [numericDraftByQuestionId, setNumericDraftByQuestionId] = useState<Record<number, string>>({});
  const [localError, setLocalError] = useState('');
  const prevOpenRef = useRef(false);

  React.useEffect(() => {
    const openingNow = open && !prevOpenRef.current;
    if (openingNow) {
      setAnswersMap(initialAnswers);
      setNumericDraftByQuestionId({});
      setLocalError('');
    }
    prevOpenRef.current = open;
  }, [open, initialAnswers]);

  const orderedQuestions = useMemo(() => getOrderedQuestions(template?.questions), [template]);

  const isQuestionAnswered = (question: SurveyQuestion): boolean => {
    if (isInstructionQuestion(question)) return true;
    const answer = answersMap[Number(question.id)];
    if (!answer) return false;
    switch (question.questionType) {
      case 'TEXT':
        return Boolean(answer.textValue && String(answer.textValue).trim());
      case 'NUMBER':
      case 'SCALE':
        return isValidNumericAnswer(answer.numericValue, question);
      case 'BOOLEAN':
        return typeof answer.booleanValue === 'boolean';
      case 'SINGLE_CHOICE':
        return typeof answer.selectedOptionId === 'number';
      case 'MULTI_CHOICE':
        return Array.isArray(answer.selectedOptionIds) && answer.selectedOptionIds.length > 0;
      default:
        return false;
    }
  };

  const setAnswer = (questionId: number, patch: Partial<ClientSurveyAnswer>) => {
    setAnswersMap((prev) => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        ...patch,
        surveyQuestionId: questionId
      }
    }));
  };

  const getNumericDisplayValue = (questionId: number, answer: ClientSurveyAnswer): string => {
    if (Object.prototype.hasOwnProperty.call(numericDraftByQuestionId, questionId)) {
      return numericDraftByQuestionId[questionId];
    }
    if (answer.numericValue == null) return '';
    return String(answer.numericValue);
  };

  const handleNumericFocus = (questionId: number, answer: ClientSurveyAnswer) => {
    const display = answer.numericValue != null ? String(answer.numericValue) : '';
    setNumericDraftByQuestionId((prev) => ({ ...prev, [questionId]: display }));
  };

  const handleNumericChange = (
    questionId: number,
    question: SurveyQuestion,
    rawValue: string,
    previousValue?: number | null
  ) => {
    const parsed = resolveNumericAnswerFromInput(rawValue, question);
    const shouldClamp =
      parsed.numericValue != null &&
      isLikelySpinnerStepChange(previousValue, parsed.numericValue, question);
    const resolved = shouldClamp
      ? resolveNumericAnswerFromInput(rawValue, question, { clamp: true })
      : parsed;

    setNumericDraftByQuestionId((prev) => ({
      ...prev,
      [questionId]: shouldClamp ? resolved.displayValue : rawValue
    }));
    setAnswer(questionId, { numericValue: resolved.numericValue });
  };

  const handleNumericBlur = (questionId: number, question: SurveyQuestion, rawValue: string) => {
    const resolved = resolveNumericAnswerFromInput(rawValue, question, { clamp: true });
    setAnswer(questionId, { numericValue: resolved.numericValue });
    setNumericDraftByQuestionId((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const handleSave = (submit: boolean) => {
    setLocalError('');
    if (!template) return;
    if (submit) {
      const missingRequired = orderedQuestions.find(
        (q) => isAnswerableQuestion(q) && q.required && !isQuestionAnswered(q)
      );
      if (missingRequired) {
        setLocalError('Debes contestar todas las preguntas obligatorias antes de enviar.');
        return;
      }
    }
    const payload = filterAnswersForBatchSave(
      normalizeNumericAnswersForSave(Object.values(answersMap), orderedQuestions),
      orderedQuestions
    );
    onSave(payload, submit);
  };

  let answerableDisplayIndex = 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Encuesta</DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Cliente: <strong>{clientDisplayName || 'N/D'}</strong>
          </Typography>
          {(error || localError) && <Alert severity="error">{error || localError}</Alert>}

          {!template ? (
            <Alert severity="warning">No hay plantilla activa disponible para aplicar la encuesta.</Alert>
          ) : orderedQuestions.length === 0 ? (
            <Alert severity="warning">La plantilla no contiene preguntas.</Alert>
          ) : (
            <Grid container spacing={2}>
              {orderedQuestions.map((question, idx) => {
                const questionId = Number(question.id);
                const answer = answersMap[questionId] || { surveyQuestionId: questionId };
                const instruction = isInstructionQuestion(question);

                if (!instruction) {
                  answerableDisplayIndex += 1;
                }

                return (
                  <Grid item xs={12} key={`question-${questionId}-${idx}`}>
                    {instruction ? (
                      <Typography variant="subtitle2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {question.questionText}
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">
                          {answerableDisplayIndex}. {question.questionText} {question.required ? '*' : ''}
                        </Typography>

                        {(question.questionType === 'NUMBER' || question.questionType === 'SCALE') && (
                          <TextField
                            type="number"
                            inputProps={getNumericCaptureInputProps(question)}
                            helperText={getNumericRangeHelperText(question)}
                            value={getNumericDisplayValue(questionId, answer)}
                            onFocus={() => handleNumericFocus(questionId, answer)}
                            onChange={(e) =>
                              handleNumericChange(
                                questionId,
                                question,
                                e.target.value,
                                answer.numericValue
                              )
                            }
                            onBlur={(e) => handleNumericBlur(questionId, question, e.target.value)}
                          />
                        )}

                        {question.questionType === 'TEXT' && (
                          <TextField
                            multiline
                            minRows={2}
                            value={answer.textValue ?? ''}
                            onChange={(e) => setAnswer(questionId, { textValue: e.target.value })}
                          />
                        )}

                        {question.questionType === 'BOOLEAN' && (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(answer.booleanValue)}
                                onChange={(e) => setAnswer(questionId, { booleanValue: e.target.checked })}
                              />
                            }
                            label={answer.booleanValue ? 'Sí' : 'No'}
                          />
                        )}

                        {question.questionType === 'SINGLE_CHOICE' && (
                          <FormControl fullWidth>
                            <Select
                              value={answer.selectedOptionId ?? ''}
                              onChange={(e) =>
                                setAnswer(questionId, {
                                  selectedOptionId:
                                    e.target.value === '' ? null : Number(e.target.value)
                                })
                              }
                            >
                              <MenuItem value="">Selecciona una opción</MenuItem>
                              {question.options.map((option) => (
                                <MenuItem key={option.id || option.sortOrder} value={option.id}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}

                        {question.questionType === 'MULTI_CHOICE' && (
                          <FormControl>
                            <Stack spacing={0.5}>
                              {question.options.map((option) => {
                                const current = answer.selectedOptionIds || [];
                                const checked = current.includes(Number(option.id));
                                return (
                                  <FormControlLabel
                                    key={option.id || option.sortOrder}
                                    control={
                                      <Switch
                                        checked={checked}
                                        onChange={(e) => {
                                          const next = e.target.checked
                                            ? [...current, Number(option.id)]
                                            : current.filter((id) => id !== Number(option.id));
                                          setAnswer(questionId, { selectedOptionIds: next });
                                        }}
                                      />
                                    }
                                    label={option.label}
                                  />
                                );
                              })}
                            </Stack>
                          </FormControl>
                        )}
                      </Stack>
                    )}
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cerrar</Button>
        <Button
          variant="outlined"
          onClick={() => handleSave(false)}
          disabled={!template || submitted || saving}
        >
          Guardar borrador
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSave(true)}
          disabled={!template || submitted || saving}
        >
          Enviar encuesta
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SurveyModal;
